import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import Video from 'twilio-video';
import * as Tools from "../../../helpers/Tools";
import { logUI } from "../../../api/andyliticsApi";
import { Typography, Icon, LinkText } from "../../ecosystem/";
import AppointmentTileHeading from "./appointmentTileHeading";
import VideoVisitTips from "./VideoVisitTips";
import VideoVisitLaunchBtn from "./VideoVisitLaunchBtn";
import VideoVisitBrowserMsgs from "./VideoVisitBrowserMsgs";
import VideoVisitRoomMsgs from "./VideoVisitRoomMsgs";
import VideoVisitRoomClosed from "./VideoVisitRoomClosed";

import ECheckInCTA from "../appointment/ECheckInCTA";
import { DashboardTile } from "../DashboardTile";
import { formatDoctorName, getFirstName } from "../../../helpers/formatting";
import AppointmentPricing from "./appointmentPricing";
import useIsTimeToStart from "./hooks/useIsTimeToStart";

const VIDEO_VISIT_CLOSED = "ROOM_CLOSE";
const VIDEO_VISIT_IN_PROGRESS = ["IN_PROGRESS"];

const AppointmentVideoVisitTile = (props) => {
    const {
        appointmentDetails,
        isMobile = false,
        isRequireEcheckin,
        isSDCVisit,
        getPhoneNumberMarkup,
        ambulatoryVideoVisitEnabled,
        SDCVisitEnabledMinutesBeforeStart,
        SDCVisitEnabledMinutesAfterStart,
        videoVisitEnabledMinutesBeforeStart,
        videoVisitEnabledMinutesAfterStart,
        ssoBaseUrl,
        videoVisitLinks
    } = props;
    const [showModal, setShowModal] = useState(false);

    const eCheckInAvailable = appointmentDetails && appointmentDetails.eCheckInStatus && appointmentDetails.eCheckInStatus.toLowerCase() !== "not yet available" ? true : false;
    const eCheckinCompleted = (appointmentDetails && appointmentDetails.eCheckInStatus) ? appointmentDetails.eCheckInStatus.toLowerCase() === "completed" : false;
    const eCheckInRequirementComplete = isRequireEcheckin ? eCheckinCompleted : true;

    let { roomOpensTime,
        roomClosingTime,
        isRoomTimePassed,
        isTimeToStart } = useIsTimeToStart(
            appointmentDetails,
            ambulatoryVideoVisitEnabled,
            eCheckinCompleted,
            isRequireEcheckin,
            isSDCVisit,
            SDCVisitEnabledMinutesBeforeStart,
            SDCVisitEnabledMinutesAfterStart,
            videoVisitEnabledMinutesBeforeStart,
            videoVisitEnabledMinutesAfterStart);

    const roomClosed = appointmentDetails && appointmentDetails.roomStatus && appointmentDetails.roomStatus === VIDEO_VISIT_CLOSED;
    const roomInProgress = (appointmentDetails && appointmentDetails.roomStatus && VIDEO_VISIT_IN_PROGRESS.indexOf(appointmentDetails.roomStatus) > -1) ? eCheckInRequirementComplete : false;
    const isRoomClosed = roomClosed || (!isTimeToStart && isRoomTimePassed);
    const apptToday = (appointmentDetails && appointmentDetails.appointmentEcheckinDays === 0);

    const apptCSNEncrypted = appointmentDetails && appointmentDetails.csnIdOfAppointmentEncrypted ? appointmentDetails.csnIdOfAppointmentEncrypted : '';
    const patientExternalId = appointmentDetails && appointmentDetails.patientExternalId ? appointmentDetails.patientExternalId : '';
    const patientName = appointmentDetails && appointmentDetails.patientName ? getFirstName(appointmentDetails.patientName) : '';

    const disabled = isRoomClosed;
    const isWebSupportedBrowser = Video.isSupported;
    const showLaunchButton = apptToday && eCheckInRequirementComplete;
    const disableLaunchButton = (!isTimeToStart && !roomInProgress) || !isWebSupportedBrowser;
    const showECheckIn = eCheckInAvailable && (!eCheckinCompleted || !showLaunchButton); //Even if not required show echeckin if not complete 

    const appointmentArrivalTime = appointmentDetails && appointmentDetails.appointmentArrivalTime ? appointmentDetails.appointmentArrivalTime : '';
    const apptTimeToShow = appointmentArrivalTime ? 'Arrive by ' + appointmentArrivalTime : appointmentDetails?.appointmenttimes;
    const disabledColor = "$color-secondary-a4";

    const toggleVideoVisitModal = () => {
        let activityCodeForModal = "MBHWeb.Dashboard.AppointmentTile.VideoVisitTips.Open";
        if (showModal) {
            activityCodeForModal = "MBHWeb.Dashboard.AppointmentTile.VideoVisitTips.Close";
        }
        logUI(activityCodeForModal, new Date(), Tools.getURLPath());
        setShowModal(prevState => !prevState);
    }

    const heading =
        <AppointmentTileHeading
            disabled={disabled}
            patientName={patientName}
            visitName={appointmentDetails?.appointmentType}
            to={{ pathname: "/visit/details", state: { csnId: apptCSNEncrypted, Id: patientExternalId } }}
            tooltip={<LinkText
                as={<button type="button"></button>}
                disabled={disabled}
                onClick={toggleVideoVisitModal}
            >
                <Icon name="information" />
                <VideoVisitTips showVideoVisitTipsModal={showModal} toggleVideoVisitTipsModal={toggleVideoVisitModal} />
            </LinkText>}
        />;

    return (
        <DashboardTile
            className={`appt-refresh-tile`}
            heading={heading}
            disabled={disabled}
        >
            <React.Fragment>
                <div className="col-12 appt-tile-body--video-visit">
                    <div className="appt-info-container row">
                        <div className={`col-12 col-xl appt-tile-body__info`}>
                            <div className="appt-tile-body__time">
                                <Typography
                                    type="heading-02"
                                    color={disabled ? disabledColor : ""}
                                >
                                    {(apptToday || appointmentDetails.appointmentEcheckinDays === 1)
                                        ? apptToday
                                            ? <span>{`Today, ${apptTimeToShow}`}</span>
                                            : <span>{`Tomorrow, ${apptTimeToShow}`}</span>
                                        : `${appointmentDetails?.appointmentDateTimeForDisplay}, ${apptTimeToShow}`}
                                </Typography>
                                {appointmentArrivalTime &&
                                    <Typography type="body-02" tag="div"
                                        color={disabled ? disabledColor : "$color-secondary-a2"}>
                                        {`Starts at ${appointmentDetails?.appointmenttimes}`}
                                    </Typography>}
                            </div>
                            <AppointmentPricing visit={appointmentDetails} />
                            {appointmentDetails && appointmentDetails?.physicianName &&
                                <div className="appt-tile-body__doc">
                                    <Typography type="heading-02" tag="div">
                                        {formatDoctorName(appointmentDetails?.physicianName)}
                                    </Typography>
                                </div>
                            }
                            {!isSDCVisit && 
                                <div className="appt-tile-body__phone">
                                    {getPhoneNumberMarkup()}
                                </div>
                            }
                            {!isRoomClosed && apptToday &&
                                (!showLaunchButton || (showLaunchButton && disableLaunchButton)) &&
                                <div className="appt-tile-body__msgs">
                                    {!isWebSupportedBrowser
                                        ? <VideoVisitBrowserMsgs
                                            ssoBaseUrl={ssoBaseUrl}
                                        />
                                        : <VideoVisitRoomMsgs
                                            eCheckinCompleted={eCheckinCompleted}
                                            isSDCVisit={isSDCVisit}
                                            roomOpensTime={roomOpensTime}
                                            roomClosingTime={roomClosingTime}
                                            roomTimePassed={isRoomTimePassed}
                                            isTimeToStart={isTimeToStart}
                                            disableLaunchButton={disableLaunchButton}
                                        />
                                    }
                                </div>
                            }
                        </div>
                        {(showECheckIn && !isRoomClosed) &&
                            <div className="col-12 col-xl-5 appt-tile-body__echeckin">
                                <ECheckInCTA
                                    appointmentDetails={appointmentDetails}
                                    eCheckInStatus={appointmentDetails.eCheckInStatus}
                                    eCheckinCompleted={eCheckinCompleted}
                                    isRequireEcheckin={isRequireEcheckin}
                                    csnIdOfAppointment={appointmentDetails.csnIdOfAppointmentEncrypted}
                                    isMobile={isMobile}
                                />
                            </div>
                        }
                        {isRoomClosed
                            ? <div className={`col-12 col-xl-5 appt-tile-body__closed-msg`}>
                                <VideoVisitRoomClosed />
                            </div>
                            : showLaunchButton &&
                            <div className={`col-12 col-xl-5 appt-tile-body__cta`}>
                                <VideoVisitLaunchBtn
                                    props={props}
                                    appointmentDetails={appointmentDetails}
                                    disabled={disableLaunchButton}
                                    isMobile={isMobile}
                                    isSDCVisit={isSDCVisit}
                                    roomClosingTime={roomClosingTime}
                                    videoVisitLinks={videoVisitLinks}
                                />
                            </div>

                        }
                    </div>
                </div>
            </React.Fragment>
        </DashboardTile>
    );
}

const mapStateToProps = (state) => {
    return {
        ambulatoryVideoVisitEnabled: state.session.serverData && state.session.serverData.ambulatoryVideoVisitEnabled ? state.session.serverData.ambulatoryVideoVisitEnabled : false,
        SDCVisitEnabledMinutesBeforeStart: state.session.serverData && state.session.serverData.SDCVisitEnabledMinutesBeforeStart ? state.session.serverData.SDCVisitEnabledMinutesBeforeStart : "5",
        SDCVisitEnabledMinutesAfterStart: state.session.serverData && state.session.serverData.SDCVisitEnabledMinutesAfterStart ? state.session.serverData.SDCVisitEnabledMinutesAfterStart : "10",
        videoVisitEnabledMinutesBeforeStart: state.session.serverData && state.session.serverData.videoVisitEnabledMinutesBeforeStart ? state.session.serverData.videoVisitEnabledMinutesBeforeStart : "30",
        videoVisitEnabledMinutesAfterStart: state.session.serverData && state.session.serverData.videoVisitEnabledMinutesAfterStart ? state.session.serverData.videoVisitEnabledMinutesAfterStart : "45",
        ssoBaseUrl: state.session.serverData ? state.session.serverData.ssoBaseURL : "",
        videoVisitLinks: state.dashboard?.videoVisitLinks
    };
};

export default withRouter(connect(mapStateToProps, null)(AppointmentVideoVisitTile));


